import { Heart, Leaf, ShieldCheck, Sprout, Target, Users, Eye } from 'lucide-react';
import Container from '../components/Container';
import SectionHeading from '../components/SectionHeading';
import { SlotPhoto } from '../components/PlaceholderTile';

// Source of truth: For Webpage_SKG.pptx ("About Us" / "Vision & Mission" slide).

const VALUES = [
  {
    icon: Heart,
    title: 'Animal Welfare',
    description: 'Ethical animal care is central to how we farm — healthy, well-cared-for herds and flocks.',
  },
  {
    icon: Leaf,
    title: 'Sustainable Agriculture',
    description: 'Zero-waste, circular farming practices that respect the land that feeds us.',
  },
  {
    icon: ShieldCheck,
    title: 'Pure & Traceable',
    description: 'Complete traceability at every step, from the farm to your family.',
  },
  {
    icon: Users,
    title: 'Rural Prosperity',
    description: 'Sharing proven farming knowledge with farmers and supporting healthier communities.',
  },
];

export default function About() {
  return (
    <section id="about" className="scroll-mt-20">
      <section className="bg-gradient-to-b from-brand-green-50 to-surface py-16 sm:py-6">
        <Container>
          <SectionHeading
            eyebrow="About Sri KalpaGavya"
            title="Nurturing Life, Harvesting Prosperity"
            subtitle="Sri KalpaGavya Dairy and Farms LLP is an integrated dairy, livestock, and sustainable farming enterprise focused on delivering pure, ethical, and high-quality farm products."
            center
          />
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-brand-gold-600">Who We Are</p>
            <h2 className="text-2xl font-bold text-brand-green-900 sm:text-3xl">Est. 2026</h2>
            <p className="mt-4 leading-relaxed text-ink-500">
              We combine responsible dairy farming, poultry, sheep rearing, natural agriculture, and value-added
              farm products with modern digital farm management practices.
            </p>
            <p className="mt-4 leading-relaxed text-ink-500">
              Our mission is to promote healthy food, animal welfare, sustainable agriculture, and rural
              prosperity through scientific farming methods, complete traceability, and continuous knowledge
              sharing with farmers. By integrating traditional farming wisdom with technology-driven monitoring
              and zero-waste circular practices, we aim to build a profitable, scalable, and environmentally
              responsible farming ecosystem.
            </p>
          </div>
          <SlotPhoto slot="about.overview" icon={Sprout} label="Add photo: Farm overview / aerial view" aspect="aspect-[4/3]" />
        </Container>
      </section>

      <section className="bg-surface-subtle py-16 sm:py-20">
        <Container className="grid gap-10 lg:grid-cols-2">
          <div className="rounded-xl bg-surface p-8 shadow-card">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-gold-100">
              <Eye className="h-6 w-6 text-brand-gold-700" />
            </div>
            <h3 className="text-xl font-bold text-brand-green-900">Our Vision</h3>
            <p className="mt-3 leading-relaxed text-ink-500">
              To become a trusted, technology-enabled integrated dairy and sustainable farming enterprise that
              nurtures healthy animals, produces pure and traceable farm products, empowers farmers with
              practical knowledge, and promotes rural prosperity through responsible and nature-friendly
              practices.
            </p>
          </div>
          <div className="rounded-xl bg-surface p-8 shadow-card">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-green-50">
              <Target className="h-6 w-6 text-brand-green-700" />
            </div>
            <h3 className="text-xl font-bold text-brand-green-900">Our Mission</h3>
            <p className="mt-3 leading-relaxed text-ink-500">
              To deliver high-quality milk, eggs, livestock, organic inputs, and value-added farm products
              through ethical animal care, scientific farming, digital farm management, and sustainable resource
              utilization — creating a profitable circular farming ecosystem while sharing proven practices with
              farmers and supporting healthier communities.
            </p>
          </div>
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container>
          <SectionHeading eyebrow="What We Stand For" title="Our farm values" center />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-xl border border-brand-green-100 bg-surface p-6 text-center shadow-card">
                <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-green-50">
                  <v.icon className="h-6 w-6 text-brand-green-700" />
                </div>
                <h3 className="text-base font-semibold text-brand-green-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-500">{v.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-surface-subtle py-16 sm:py-20">
        <Container className="grid items-center gap-10 lg:grid-cols-2">
          <SlotPhoto slot="about.team" icon={Users} label="Add photo: Farm team / partners" aspect="aspect-[4/3]" />
          <div>
            <SectionHeading eyebrow="Our Promise" title="What we're rooted in" />
            <p className="mt-6 text-lg leading-relaxed text-ink-700">
              Rooted in quality, guided by sustainability, committed to animal welfare, and driven by community
              prosperity.
            </p>
          </div>
        </Container>
      </section>
    </section>
  );
}
